import { useQueryGetAllUsers } from "@apis:user/user";
import { SuperDataGrid } from "@components:SuperDataGrid";
import { usePaginationSortDataGrid } from "@hooks:usePaginationSortDataGrid";
import { useTableFilter } from "@hooks:useTableFilter";
import { Box, useTheme } from "@mui/material";
import { GridActionsCellItem, GridColDef } from "@mui/x-data-grid";
import { UserDeleteModal } from "@pages:settings/Tenent/Users/UserDeleteModal";
import { UserModal } from "@pages:settings/Tenent/Users/UserModal";
import { useUsersData } from "@stores:settings/users";
import { formatDateTime } from "@utils:datetime";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { MdDelete, MdModeEdit } from "react-icons/md";

export const UsersTable = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [isOpenModalToUpdate, setIsOpenModalToUpdate] = useState(false);
  const [isOpenModalToDelete, setIsOpenModalToDelete] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const { searchKeyword, state } = useUsersData();
  const { paginationProps, sortProps } = usePaginationSortDataGrid({
    pageSize: 30,
    defaultSortModel: {
      field: "name",
      sort: "asc",
    },
  });

  const { data, isLoading } = useQueryGetAllUsers();

  const cleanedData = useTableFilter({
    filters: [
      {
        field: "enabled",
        operator: "equals",
        value: state === "active",
      },
      {
        field: "email",
        operator: "contains",
        value: searchKeyword,
      },
    ],
    data,
  });

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: "name",
        headerName: t("common.name"),
        flex: 1,
        cellClassName: "font-mono",
      },
      {
        field: "email",
        headerName: t("common.email"),
        flex: 1,
        cellClassName: "font-mono",
      },
      {
        field: "role",
        headerName: t("common.role"),
        flex: 1,
        valueGetter: (_value, row) =>
          t(`tenant.role.${row.role}`, { defaultValue: row.role }),
        cellClassName: "font-mono",
      },
      {
        field: "createdAt",
        headerName: t("common.createdAt"),
        flex: 1,
        valueParser: (_value, row) => row.createdAt,
        valueGetter: (_value, row) => formatDateTime(row.createdAt),
        cellClassName: "font-mono",
      },
      {
        field: "updatedAt",
        headerName: t("common.updatedAt"),
        flex: 1,
        valueParser: (_value, row) => row.updatedAt,
        valueGetter: (_value, row) => formatDateTime(row.updatedAt),
        cellClassName: "font-mono",
      },
      {
        field: "actions",
        type: "actions",
        headerName: "",
        align: "right",
        flex: 1,
        getActions: (params: any) => {
          const editButton = (
            <GridActionsCellItem
              key="edit"
              icon={<MdModeEdit size={20} />}
              label={t("common.edit")}
              className="textPrimary"
              color="primary"
              onClick={() => {
                setIsOpenModalToUpdate(true);
                setSelectedUserId(params.row.id);
              }}
            />
          );

          const deleteButton = (
            <GridActionsCellItem
              key="delete"
              icon={<MdDelete size={20} />}
              label={t("common.delete")}
              style={{ color: theme.palette.error.main }}
              onClick={async () => {
                setIsOpenModalToDelete(true);
                setSelectedUserId(params.row.id);
              }}
            />
          );

          const actions = [editButton];
          if (params?.row?.enabled) {
            actions.push(deleteButton);
          }

          return actions;
        },
      },
    ],
    [t, theme.palette.error.main],
  );

  return (
    <Box
      sx={{
        height: "100%",
      }}
    >
      <SuperDataGrid
        columns={columns}
        rows={cleanedData}
        loading={isLoading}
        {...paginationProps}
        {...sortProps}
        for="users"
        noRowsOverlayText={t("tenant.users.noneFound")}
        sx={{
          "& .MuiDataGrid-row--borderBottom": {
            backgroundColor: "white !important",
          },
        }}
      />
      {isOpenModalToUpdate && (
        <UserModal
          isOpen={isOpenModalToUpdate}
          onClose={() => setIsOpenModalToUpdate(false)}
          userId={selectedUserId!}
        />
      )}
      {isOpenModalToDelete && (
        <UserDeleteModal
          isOpen={isOpenModalToDelete}
          onClose={() => setIsOpenModalToDelete(false)}
          userId={selectedUserId!}
        />
      )}
    </Box>
  );
};
